import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LogoutRounded, Menu, Close } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import ConfirmDialog from "../common/ConfirmDialog.jsx";
import { useScrollLock } from "../common/lenisContext";
import { ACCOUNT_SETTINGS_SECTIONS } from "./accountSettingsNav";
import AccountSettingsSidebarHeader from "./AccountSettingsSidebarHeader";
import {
    ACCOUNT_PAGE_CARD_NOISE_SX,
    ACCOUNT_PAGE_MOBILE_MQ,
    ACCOUNT_PAGE_SIDEBAR_LOGOUT_HAIRLINE_SX,
    ACCOUNT_PAGE_SIDEBAR_LOGOUT_SX,
    ACCOUNT_PAGE_SIDEBAR_NAV_SX,
    ACCOUNT_PAGE_SIDEBAR_SPACER_SX,
    ACCOUNT_PAGE_SIDEBAR_SX,
    SETTINGS_SIDEBAR_NAV_INSET,
    SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
} from "./accountPageStyles";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const ACCOUNT_SETTINGS_SIDEBAR_SECTIONS = ACCOUNT_SETTINGS_SECTIONS.filter(
    (section) => section.id !== "my-reports",
);

const ACTIVE_ITEM_BACKGROUND =
    "linear-gradient(180deg, rgba(28, 24, 16, 0.96) 0%, rgba(22, 19, 12, 0.98) 100%)";

const ACTIVE_TAB_INDICATOR_SX = {
    position: "absolute",
    left: 0,
    top: "8px",
    bottom: "8px",
    width: "3px",
    borderRadius: "2px",
    background: colors.primary.main,
    opacity: 0,
    zIndex: 1,
    pointerEvents: "none",
    transition: "opacity 0.15s ease-in-out",
};

const TAB_BUTTON_SX = {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.65rem",
    width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET} * 2))`,
    m: 0,
    mx: SETTINGS_SIDEBAR_NAV_INSET,
    px: "0.875rem",
    py: "0.75rem",
    border: "none",
    outline: "none",
    borderRadius: "10px",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    boxSizing: "border-box",
    "&:focus, &:focus-visible": {
        outline: "none",
    },
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET_MOBILE} * 2))`,
        mx: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
        px: "0.875rem",
        py: "0.7rem",
    },
};

const LOGOUT_BUTTON_SX = {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET} * 2))`,
    mx: SETTINGS_SIDEBAR_NAV_INSET,
    mb: 0,
    px: "12px",
    py: "9px",
    border: "none",
    outline: "none",
    borderRadius: "7px",
    background: "transparent",
    color: "rgba(255,255,255,0.40)",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    boxSizing: "border-box",
    transition: "color 160ms ease, background 160ms ease",
    "&:hover": {
        color: colors.state.danger.main,
        background: alpha(colors.state.danger.hover, 0.14),
    },
    "&:active": {
        background: alpha(colors.state.danger.hover, 0.18),
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET_MOBILE} * 2))`,
        mx: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
    },
};

const TAB_LABEL_SX = {
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    minWidth: 0,
};

const LOGOUT_LABEL_SX = {
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.01em",
    lineHeight: 1.2,
};

const MENU_ICON_BUTTON_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2rem",
    height: "2rem",
    border: "none",
    outline: "none",
    borderRadius: "6px",
    background: "rgba(255,255,255,0.04)",
    color: colors.text.secondary,
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover": {
        background: "rgba(255,255,255,0.08)",
        color: colors.text.primary,
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

const DRAWER_OVERLAY_SX = {
    position: "fixed",
    inset: 0,
    zIndex: 1500,
    backgroundColor: "rgba(0,0,0,0.5)",
};

const DRAWER_PANEL_SX = {
    position: "fixed",
    inset: 0,
    zIndex: 1501,
    display: "flex",
    flexDirection: "column",
    background: `
        linear-gradient(
            180deg,
            rgba(10,10,12,0.99) 0%,
            rgba(6,6,8,1) 100%
        )
    `,
    overflowY: "auto",
    overflowX: "hidden",
};

function TabList({ sections, activeSection, onSelect, onLogoutClick }) {
    return (
        <>
            <Box
                component="nav"
                aria-label="Account settings"
                sx={ACCOUNT_PAGE_SIDEBAR_NAV_SX}
            >
                {sections.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                        <Box
                            key={section.id}
                            sx={{
                                position: "relative",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: SETTINGS_SIDEBAR_NAV_INSET,
                                    right: SETTINGS_SIDEBAR_NAV_INSET,
                                    borderRadius: "10px",
                                    background: ACTIVE_ITEM_BACKGROUND,
                                    opacity: isActive ? 1 : 0,
                                    zIndex: 0,
                                    pointerEvents: "none",
                                    transition: "opacity 0.15s ease-in-out",
                                    [ACCOUNT_PAGE_MOBILE_MQ]: {
                                        left: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
                                        right: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
                                    },
                                }}
                            />
                            <Box
                                aria-hidden
                                sx={{
                                    ...ACTIVE_TAB_INDICATOR_SX,
                                    opacity: isActive ? 1 : 0,
                                }}
                            />
                            <Box
                                component="button"
                                type="button"
                                aria-current={isActive ? "page" : undefined}
                                onClick={() => onSelect(section.id)}
                                sx={{
                                    ...TAB_BUTTON_SX,
                                    color: isActive
                                        ? colors.text.primary
                                        : colors.text.muted,
                                    "&:hover": {
                                        background: isActive
                                            ? "transparent"
                                            : "rgba(255,255,255,0.04)",
                                    },
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        ...TAB_LABEL_SX,
                                        fontWeight: isActive ? 600 : 500,
                                        color: "inherit",
                                    }}
                                >
                                    {section.label}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <Box sx={ACCOUNT_PAGE_SIDEBAR_SPACER_SX} />
            <Box sx={ACCOUNT_PAGE_SIDEBAR_LOGOUT_SX}>
                <Box sx={ACCOUNT_PAGE_SIDEBAR_LOGOUT_HAIRLINE_SX} />
                <Box
                    component="button"
                    type="button"
                    aria-label="Logout"
                    onClick={onLogoutClick}
                    sx={LOGOUT_BUTTON_SX}
                >
                    <LogoutRounded
                        sx={{
                            fontSize: "1.5rem",
                            opacity: 0.8,
                            flexShrink: 0,
                        }}
                    />
                    <Typography component="span" sx={LOGOUT_LABEL_SX}>
                        Logout
                    </Typography>
                </Box>
            </Box>
        </>
    );
}

TabList.propTypes = {
    sections: PropTypes.array.isRequired,
    activeSection: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onLogoutClick: PropTypes.func.isRequired,
};

function AccountSettingsSidebar({
    activeSection,
    onSectionChange,
    user,
    membership,
    avatarCacheBust,
    logout,
    logoutBlocked = false,
    onLogoutBlocked,
    mobileEmbedded = false,
}) {
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);

    useScrollLock(Boolean(isMobile && drawerOpen));

    const handleLogoutClick = useCallback(() => {
        if (logoutBlocked) {
            onLogoutBlocked?.();
            return;
        }
        setLogoutDialogOpen(true);
    }, [logoutBlocked, onLogoutBlocked]);

    const handleConfirmLogout = useCallback(() => {
        setLogoutDialogOpen(false);
        logout?.();
    }, [logout]);

    const handleCancelLogout = useCallback(() => {
        setLogoutDialogOpen(false);
    }, []);

    const handleSectionSelect = useCallback(
        (sectionId) => {
            onSectionChange?.(sectionId);
            setDrawerOpen(false);
        },
        [onSectionChange],
    );

    const mobileHeader = isMobile ? (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                px: "0.7rem",
                pt: "1.25rem",
                pb: "0.8rem",
            }}
        >
            <Typography
                sx={{
                    fontSize: "1.15rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    lineHeight: 1.15,
                }}
            >
                Settings
            </Typography>
            <Box
                component="button"
                type="button"
                aria-label="Open settings menu"
                onClick={() => setDrawerOpen(true)}
                sx={MENU_ICON_BUTTON_SX}
            >
                <Menu sx={{ fontSize: "1.15rem" }} />
            </Box>
        </Box>
    ) : null;

    return (
        <>
            {isMobile && mobileEmbedded ? (
                mobileHeader
            ) : (
                <Box sx={ACCOUNT_PAGE_SIDEBAR_SX}>
                    <Box sx={ACCOUNT_PAGE_CARD_NOISE_SX} />

                    {isMobile ? (
                        mobileHeader
                    ) : (
                        <>
                            <AccountSettingsSidebarHeader
                                user={user}
                                membership={membership}
                                avatarCacheBust={avatarCacheBust}
                            />
                            <TabList
                                sections={ACCOUNT_SETTINGS_SIDEBAR_SECTIONS}
                                activeSection={activeSection}
                                onSelect={handleSectionSelect}
                                onLogoutClick={handleLogoutClick}
                            />
                        </>
                    )}
                </Box>
            )}

            {/* Mobile drawer overlay */}
            {isMobile && ReactDOM.createPortal(
            <AnimatePresence>
                {drawerOpen && (
                    <>
                        <MotionBox
                            key="drawer-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            onClick={() => setDrawerOpen(false)}
                            sx={DRAWER_OVERLAY_SX}
                        />
                        <MotionBox
                            key="drawer-panel"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 0.2,
                                ease: "easeOut",
                            }}
                            sx={DRAWER_PANEL_SX}
                        >
                            <Box
                                sx={{
                                    pt: "env(safe-area-inset-top, 0px)",
                                }}
                            >
                                <AccountSettingsSidebarHeader
                                    user={user}
                                    membership={membership}
                                    avatarCacheBust={avatarCacheBust}
                                    trailingAction={
                                        <IconButton
                                            aria-label="Close settings menu"
                                            onClick={() => setDrawerOpen(false)}
                                            sx={{
                                                width: "1.9rem",
                                                height: "1.9rem",
                                                borderRadius: "0.55rem",
                                                color: colors.text.secondary,
                                                backgroundColor: "rgba(255, 255, 255, 0.04)",
                                                "&:hover": {
                                                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                                                },
                                            }}
                                        >
                                            <Close sx={{ fontSize: "0.95rem" }} />
                                        </IconButton>
                                    }
                                />
                            </Box>
                            <TabList
                                sections={ACCOUNT_SETTINGS_SIDEBAR_SECTIONS}
                                activeSection={activeSection}
                                onSelect={handleSectionSelect}
                                onLogoutClick={handleLogoutClick}
                            />
                        </MotionBox>
                    </>
                )}
            </AnimatePresence>,
            document.body)}

            <ConfirmDialog
                open={logoutDialogOpen}
                title="Logout"
                message="Are you sure you want to log out?"
                confirmText="Logout"
                cancelText="Cancel"
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
            />
        </>
    );
}

AccountSettingsSidebar.propTypes = {
    activeSection: PropTypes.string,
    onSectionChange: PropTypes.func,
    user: PropTypes.object,
    membership: PropTypes.object,
    avatarCacheBust: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    logout: PropTypes.func,
    logoutBlocked: PropTypes.bool,
    onLogoutBlocked: PropTypes.func,
    mobileEmbedded: PropTypes.bool,
};

export default React.memo(AccountSettingsSidebar);
